import { z } from 'zod';

/**
 * Léxico das dimensões de benefício de uma família tarifária — a ÚNICA fonte
 * para classificar texto livre do fornecedor no genui-kit (estados da escada), e
 * que SERÁ compartilhada com o modal-session (coerência texto × enum) quando ele
 * migrar para cá. Antes eram duas tabelas de regex, uma em cada lado, e a do kit
 * casava "remarcacao" como assento (`marcacao` ⊂ `remarcacao`).
 *
 * Fora destas cinco dimensões a frase NÃO é classificada: chutar uma dimensão é
 * afirmar o que ninguém disse.
 */
const FareDimensionSchema = z.enum(['carry', 'checked', 'seat', 'change', 'refund']);

export type FareDimension = z.infer<typeof FareDimensionSchema>;

/** Ordem VISUAL: bagagem (mão → despacho) → assento → flexibilidade. */
export const FARE_DIMENSIONS: readonly FareDimension[] = FareDimensionSchema.options;

/** Como a frase trata UMA dimensão. `conflict` é a frase que se contradiz
 *  ("checked baggage included; checked baggage not included"): nem verde nem
 *  vermelho, porque as duas cores seriam mentira. */
export type FarePolarity = 'affirmed' | 'negated' | 'conflict';

interface FareDimensionPolarity {
  dimension: FareDimension;
  polarity: FarePolarity;
}

const norm = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '')
    .replaceAll(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

/** pt-BR, en, es. "assento" sem "marcacao": "Remarcação" pertence a `change`. */
const PATTERNS: Record<FareDimension, RegExp> = {
  carry: /\bmao\b|carry ?on|hand ?bag|cabin bag|\bmano\b/,
  checked: /despach|\bchecked|bodega|facturad/,
  seat: /assento|asiento|\bseat/,
  change: /alterac|remarc|altera|\bchange|\bcambio/,
  refund: /reembols|refund|devoluc/,
};

/** Clones com `g` porque a polaridade é por OCORRÊNCIA e `matchAll` exige a
 *  flag. Ficam separados de `PATTERNS` para que ninguém arraste `lastIndex`
 *  para um `.test()` e receba um falso negativo na chamada seguinte. */
const SCANNERS = new Map<FareDimension, RegExp>(
  FARE_DIMENSIONS.map((d): [FareDimension, RegExp] => [d, new RegExp(PATTERNS[d].source, 'g')]),
);

/** Preposições e coordenativas negativas. "nem"/"ni"/"nor" REPETEM a negação
 *  ("sem bagagem de mão nem despacho"); "e"/"y"/"and"/"or" não são negação
 *  nenhuma e por isso ficam de fora. */
const LEADING_ONLY = /\b(sem|sin|non|nem|ni|nor)\b/g;

/** Mudam de lado conforme a palavra SEGUINTE: "não permitido" nega o que veio
 *  antes, "não reembolsável" nega o que vem depois. */
const AMBIGUOUS = /\b(nao|no|not)\b/g;

const TRAILING_ONLY = /\b(indisponive\w*|negad\w*|proibid\w*|unavailable|excluid\w*)/g;

/** Particípios que fecham a frase ("reembolso não PERMITIDO"): quando um deles
 *  segue a negativa ambígua, ela é posposta. `reembolsavel`/`refundable` estão
 *  deliberadamente FORA — "não reembolsável" é negativa anteposta colada na
 *  palavra, e lê-la como posposta a mandaria caçar dimensão à esquerda. */
const PREDICATE_STEMS =
  /^(incluid|inclus|permitid|disponive|disponible|aplicave|aplicable|autorizad|possive|posible|oferecid|contemplad)\w*$/;
const PREDICATE_WORDS =
  /^(included|permitted|allowed|available|applicable|authorized|possible|offered)$/;
const isPredicate = (word: string): boolean =>
  PREDICATE_STEMS.test(word) || PREDICATE_WORDS.test(word);

/** Havendo taxa no caminho, a negativa é da TAXA e não do benefício: "sem taxa
 *  de reembolso" reembolsa de graça, "marcação de assento: paga" tem assento. */
const FEE_WORDS = /\b(taxa|taxas|fee|fees|cargo|multa|custo|cost|pago|paga|paid)\b/;

/** Negações coladas à palavra de taxa dizem que NÃO há taxa: "sem taxa", "no
 *  fee", "isento de multa", "free of charge". Taxa negada não é taxa. */
const FEE_NEGATED: readonly RegExp[] = [
  /\b(sem|sin|no|not|nao|without)\s+(?:\w+\s+){0,2}?(taxa|taxas|fee|fees|cargo|multa|custo|cost|charge|charges)\b/,
  /\b(isent[oa]s?|libre)\s+de\s+(?:\w+\s+){0,2}?(taxa|taxas|fee|fees|cargo|multa|custo|cost|charge|charges)\b/,
  /\bfree\s+of\s+(?:\w+\s+){0,2}?(taxa|taxas|fee|fees|cargo|multa|custo|cost|charge|charges)\b/,
];

/** Percentual PARCIAL ("Reembolso: 90%", "(taxa 10%)"). 100% não é custo. */
const PARTIAL_PERCENT = /\b(?:[1-9]|[1-9]\d)\s?%/;

/** Se a frase declara um CUSTO para o que afirma: palavra de taxa não negada, ou
 *  percentual parcial. Não olha dimensão nem polaridade — quem chama já sabe que
 *  a frase afirma o benefício; isto só diz que ela o afirma COM taxa. É a segunda
 *  e ÚLTIMA regra de texto: o texto do fornecedor é fallback, não fonte, e não
 *  ganha mais vocabulário (ver AGENTS.md do modal-session).
 *
 *  `PARTIAL_PERCENT` testa o texto CRU, antes de normalizar: `norm` descarta todo
 *  caractere que não é letra/dígito, o que inclui o `%` — testar contra o texto
 *  normalizado deixava este ramo morto ("Reembolso: 90%" nunca batia). */
export function mentionsFee(text: string): boolean {
  if (PARTIAL_PERCENT.test(text)) return true;
  const n = norm(text);
  return FEE_WORDS.test(n) && !FEE_NEGATED.some((r) => r.test(n));
}

const WINDOW = 32;

interface Occurrence {
  dimension: FareDimension;
  start: number;
  end: number;
}

interface NegationToken {
  start: number;
  end: number;
  kind: 'leading' | 'trailing';
}

function occurrencesIn(n: string): Occurrence[] {
  const found: Occurrence[] = [];
  for (const [dimension, scanner] of SCANNERS) {
    for (const m of n.matchAll(scanner)) {
      found.push({ dimension, start: m.index, end: m.index + m[0].length });
    }
  }
  return found.sort((a, b) => a.start - b.start);
}

const nextWord = (n: string, end: number): string => n.slice(end).trimStart().split(' ')[0] ?? '';

/** Início da palavra ANTERIOR à negativa — a guarda de taxa precisa dela porque
 *  a taxa costuma vir antes ("Multa no reembolso", "Não cobra taxa de ..."). */
function wordStartBefore(n: string, tokenStart: number): number {
  return n.slice(0, tokenStart).trimEnd().lastIndexOf(' ') + 1;
}

function negationsIn(n: string): NegationToken[] {
  const tokens: NegationToken[] = [];
  const push = (m: RegExpExecArray, kind: NegationToken['kind']): void => {
    tokens.push({ start: m.index, end: m.index + m[0].length, kind });
  };
  for (const m of n.matchAll(LEADING_ONLY)) push(m, 'leading');
  for (const m of n.matchAll(TRAILING_ONLY)) push(m, 'trailing');
  for (const m of n.matchAll(AMBIGUOUS)) {
    push(m, isPredicate(nextWord(n, m.index + m[0].length)) ? 'trailing' : 'leading');
  }
  return tokens;
}

/** A negativa ANTEPOSTA pertence à ocorrência mais próxima à direita, dentro da
 *  janela — nunca à esquerda: "Reembolso permitido sem restrições" viraria
 *  vermelho, e "Alterações sem taxa" também. Sem ocorrência à direita ela não
 *  nega ninguém. */
function attachRight(
  n: string,
  token: NegationToken,
  occurrences: readonly Occurrence[],
): Occurrence | undefined {
  const target = occurrences.find((o) => o.start >= token.end && o.start - token.end <= WINDOW);
  if (!target) return undefined;
  const span = n.slice(wordStartBefore(n, token.start), target.start);
  return FEE_WORDS.test(span) ? undefined : target;
}

/** A negativa POSPOSTA pertence à ocorrência mais próxima à esquerda — é isto
 *  que cerca a negativa na cláusula dela ("Bagagem despachada não incluída;
 *  bagagem de mão incluída" nega o despacho, não a mão). Quando ela ABRE a
 *  frase não existe esquerda ("Indisponível para reembolso"): só nesse caso
 *  olha para a direita. */
function attachLeft(
  n: string,
  token: NegationToken,
  occurrences: readonly Occurrence[],
): Occurrence | undefined {
  for (let i = occurrences.length - 1; i >= 0; i--) {
    const o = occurrences[i];
    if (!o || o.end > token.start) continue;
    if (token.start - o.end > WINDOW) break;
    return FEE_WORDS.test(n.slice(o.end, token.start)) ? undefined : o;
  }
  return token.start === 0 ? attachRight(n, token, occurrences) : undefined;
}

function aggregate(total: number, negated: number): FarePolarity {
  if (negated === 0) return 'affirmed';
  return negated === total ? 'negated' : 'conflict';
}

/**
 * Dimensões que a frase toca, na ordem VISUAL de `FARE_DIMENSIONS`, cada uma com
 * a polaridade agregada das suas ocorrências. Vazio quando a frase não toca
 * nenhuma.
 *
 * Por OCORRÊNCIA, e não por dimensão, porque a mesma palavra pode aparecer duas
 * vezes dizendo coisas opostas; e cada negativa gruda em no MÁXIMO uma
 * ocorrência (a mais próxima do lado certo), que é o que a impede de atravessar
 * para a cláusula vizinha. O preço é a dimensão distante de uma negativa que
 * cobria as duas ("no checked bag or seat selection") escapar como afirmada —
 * deliberado: esconder um vermelho é o erro barato, inventar um é o caro. Quando
 * o enum do fornecedor nega a frase, quem consome lê essa afirmação duvidosa como
 * "não informado"; quando o enum afirma, o enum decide — texto só rebaixa.
 */
export function polaritiesOf(text: string): FareDimensionPolarity[] {
  const n = norm(text);
  const occurrences = occurrencesIn(n);
  if (occurrences.length === 0) return [];

  const negated = new Set<Occurrence>();
  for (const token of negationsIn(n)) {
    const target =
      token.kind === 'leading'
        ? attachRight(n, token, occurrences)
        : attachLeft(n, token, occurrences);
    if (target) negated.add(target);
  }

  return FARE_DIMENSIONS.flatMap((dimension) => {
    const mine = occurrences.filter((o) => o.dimension === dimension);
    if (mine.length === 0) return [];
    const hits = mine.filter((o) => negated.has(o)).length;
    return [{ dimension, polarity: aggregate(mine.length, hits) }];
  });
}

/** Gramática do RÓTULO nu: a frase inteira é um nome de dimensão, e só. Tudo o
 *  que não casar aqui — "Reembolso parcial", "Reembolsável", "Bagagem despachada
 *  incluída" — diz algo sobre o benefício e pode contradizer um enum que nega.
 *  Lista FECHADA de propósito: o texto do fornecedor não ganha mais vocabulário, e
 *  um modificador desconhecido nunca vira rótulo por omissão. Casada contra o
 *  texto JÁ normalizado (acentos e pontuação já caíram em `norm`). */
const BARE_LABELS: readonly RegExp[] = [
  /^(reembolsos?|refunds?|devolucion(es)?|cancelamentos?|cancellations?|cancelacion(es)?)$/,
  /^(alteracao|alteracoes|remarcacao|remarcacoes|mudancas?|changes?|cambios?)$/,
  /^(assentos?|seats?|asientos?|(marcacao|selecao|escolha) de assento|seat selection|seleccion de asiento)$/,
  /^(bagagem( despachada| de mao)?|malas?( de mao)?|luggage|baggage|despacho)$/,
  /^(checked (bags?|baggage|luggage)|carry ?on( bags?| baggage)?)$/,
  /^(hand (luggage|baggage)|equipaje( facturado| de mano)?)$/,
];

/**
 * Frase feita SÓ das palavras da própria dimensão ("Reembolso", "Bagagem
 * despachada", "Alteração", "Assento"): um RÓTULO ao lado do enum, não uma
 * afirmação. Quem chama deixa o enum decidir esse caso — só o texto que afirma
 * algo além do nome ("Tarifa 100% reembolsável", "Bagagem despachada incluída",
 * "Reembolso: 100%", "Reembolsável", "Reembolso parcial") pode contradizer um
 * enum que nega. Gramática FECHADA (`BARE_LABELS`) em vez de detecção de sinais:
 * um sinal por sinal nunca cobre todo modificador possível ("parcial" escapava),
 * e o texto do fornecedor não ganha mais vocabulário — um modificador
 * desconhecido nunca vira rótulo por omissão.
 */
export function isBareMention(text: string): boolean {
  const label = norm(text).trim();
  return BARE_LABELS.some((r) => r.test(label));
}
